import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AddressAreaComponent } from './address-area/address-area.component';
import { AddressesMapComponent } from './addresses-map/addresses-map.component';
import { AirtableMapComponent } from './airtable-map/airtable-map.component';
import { AppRoutes } from './app-route-data';
import { authGuard, projectAccess, projectOverviewAccess, requireAnyFunctionality, requireFeature } from './auth.guard';
import { CanDeactivateForm } from './can-deactivate-form';
import { ChatComponent } from './chat/chat/chat.component';
import { ContactDetailComponent } from './contacts/contact-detail/contact-detail.component';
import { ContactEditComponent } from './contacts/contact-edit/contact-edit.component';
import { ContactOverviewComponent } from './contacts/contact-overview/contact-overview.component';
import { CustomerDetailComponent } from './customer-detail/customer-detail.component';
import { CustomerListComponent } from './customer-list/customer-list.component';
import { CustomerProjectConsumersComponent } from './customer-project-consumers/customer-project-consumers.component';
import { CustomerProjectDetailComponent } from './customer-project-detail/customer-project-detail.component';
import { CustomerProjectManageComponent } from './customer-project-manage/customer-project-manage.component';
import { CustomerProjectOverviewComponent } from './customer-project-overview/customer-project-overview.component';
import { CustomerProjectRequestAccessComponent } from './customer-project-request-access/customer-project-request-access.component';
import { CustomerReportComponent } from './customer-report/customer-report.component';
import { CustomerTableComponent } from './customer-table/customer-table.component';
import { CustomersOverviewComponent } from './customers-overview/customers-overview.component';
import { DocumentsComponent } from './documents/documents.component';
import { ImportExcelComponent } from './import-excel/import-excel.component';
import { ImportShapefileComponent } from './import-shapefile/import-shapefile.component';
import { LocalStorage } from './local-storage.class';
import { LoginCodeComponent } from './login-code/login-code.component';
import { LoginComponent } from './login/login.component';
import { LogoutComponent } from './logout/logout.component';
import { MicrosoftCallbackComponent } from './microsoft-callback/microsoft-callback.component';
import { NewsDetailComponent } from './news/news-detail/news-detail.component';
import { NewsEditComponent } from './news/news-edit/news-edit.component';
import { NewsComponent } from './news/news.component';
import { ProjectsComponent } from './projects/projects.component';
import { QrComponent } from './qr/qr.component';
import { RegisterComponent } from './register/register.component';
import { Routenames } from './route-names.enum';
import { RouteParamnames } from './route-param-names.enum';
import { AuthorisationService } from './services/auth/authorisation.service';
import { Features } from './services/auth/features';
import { Functionalities } from './services/auth/functionalities';
import { Roles } from './services/auth/roles';
import { SettingsComponent } from './settings/settings.component';
import { SurveyAnswersComponent } from './survey/survey-answers/survey-answers.component';
import { SurveyEditComponent } from './survey/survey-edit/survey-edit.component';
import { TakeOverComponent } from './take-over/take-over.component';
import { CustomerTimelineComponent } from './timeline/customer-timeline/customer-timeline.component';
import { TimelineDetailComponent } from './timeline/timeline-detail/timeline-detail.component';
import { TimelineEditComponent } from './timeline/timeline-edit/timeline-edit.component';
import { TimelineComponent } from './timeline/timeline/timeline.component';
import { WorkareaComponent } from './workarea/workarea.component';

const desktopSearch = () => window.matchMedia('(min-width: 768px)').matches;
export const desktopOnly = () => window.matchMedia('(min-width: 769px)').matches && !Capacitor.isNativePlatform();
const screenshotChildren: AppRoutes = [
    { path: Routenames.list, component: CustomerListComponent },
    { path: Routenames.table, component: CustomerTableComponent },
    { path: Routenames.report, component: CustomerReportComponent },
];

const routes: AppRoutes = [
    // Routes without an existing session
    {
        path: Routenames.login,
        data: { requiresExistingSession: false },
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: LoginComponent,
                data: { title: 'Welkom bij Dirk', search: false, hideMenu: true },
            },
            {
                path: Routenames.code,
                component: LoginCodeComponent,
                data: { title: 'Inloggen met code', search: false, hideMenu: true },
            },
            {
                path: Routenames.entra,
                component: MicrosoftCallbackComponent,
                data: { title: 'Inloggen', search: false, hideMenu: true },
            },
        ],
    },
    {
        path: Routenames.register,
        data: { requiresExistingSession: false },
        children: [
            {
                path: `:${RouteParamnames.customerId}`,
                children: [
                    {
                        path: `:${RouteParamnames.projectId}`,
                        component: RegisterComponent,
                        data: { title: 'Account aanmaken', search: false, hideMenu: true },
                    },
                ],
            },
        ],
    },

    // Routes authenticated by their required URL token
    {
        path: Routenames.customersScreenshot,
        data: { requiresExistingSession: false },
        children: [
            {
                path: `:${RouteParamnames.customerId}`,
                children: [
                    {
                        path: `:${RouteParamnames.token}`,
                        component: CustomerDetailComponent,
                        children: screenshotChildren,
                        data: { title: 'Kaart', header: false },
                    },
                ],
            },
        ],
    },
    {
        path: Routenames.addressArea,
        data: { requiresExistingSession: false },
        children: [
            {
                path: `:${RouteParamnames.addressAreaId}`,
                children: [
                    {
                        path: `:${RouteParamnames.token}`,
                        component: AddressAreaComponent,
                        data: { title: 'Adresgebied', header: false },
                    },
                ],
            },
        ],
    },
    {
        path: Routenames.qr,
        component: QrComponent,
        data: { requiresExistingSession: false, title: 'Dirk app installeren', search: false, hideMenu: true },
    },
    {
        path: Routenames.logout,
        component: LogoutComponent,
        data: { requiresExistingSession: false, header: false, hideMenu: true },
    },
    {
        path: Routenames.takeOver,
        component: TakeOverComponent,
        data: { requiresExistingSession: false, title: 'Inloggen', search: false, hideMenu: true },
    },

    // Authenticated application
    {
        path: '',
        canActivateChild: [authGuard],
        children: [
            {
                // Kies alleen een bestemming waarvan de guard ook echt doorlaat. Kijkt dit
                // naar iets anders dan de guard, dan stuurt die terug naar '' en ontstaat
                // een redirect-lus die Angular stil afbreekt.
                path: '',
                pathMatch: 'full',
                redirectTo: () =>
                    AuthorisationService.hasFeature(Features.CustomersRecentSorted)
                        ? Routenames.customers
                        : AuthorisationService.hasAnyFunctionality(Functionalities.Timeline)
                          ? Routenames.timeline
                          : Routenames.settings,
            },
            {
                path: `${Routenames.customers}/:${RouteParamnames.customerId}/${Routenames.projects}/:${RouteParamnames.projectId}/${Routenames.timeline}`,
                component: CustomerTimelineComponent,
                canActivate: [requireAnyFunctionality(Functionalities.Timeline)],
                data: { title: 'Tijdlijn', search: false, menuId: Routenames.timeline },
                children: [
                    {
                        path: `:${RouteParamnames.timelineId}`,
                        children: [
                            { path: '', pathMatch: 'full', component: TimelineDetailComponent },
                            {
                                path: Routenames.edit,
                                canActivate: [requireAnyFunctionality(Functionalities.TimelineManagement)],
                                component: TimelineEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Tijdlijn bewerken' },
                            },
                            {
                                path: Routenames.copy,
                                canActivate: [requireAnyFunctionality(Functionalities.TimelineManagement)],
                                component: TimelineEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Tijdlijn kopiëren' },
                            },
                        ],
                    },
                ],
            },
            {
                path: Routenames.customers,
                canActivate: [requireFeature(Features.CustomersRecentSorted)],
                data: {
                    menu: {
                        id: Routenames.customers,
                        label: 'Klantgebieden',
                        icon: 'home',
                        visible: () => AuthorisationService.hasFeature(Features.CustomersRecentSorted),
                    },
                    search: () => AuthorisationService.hasFeature(Features.CustomersRecentSorted),
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        component: CustomersOverviewComponent,
                        data: { title: 'Klantgebieden' },
                    },
                    {
                        path: `:${RouteParamnames.customerId}`,
                        children: [
                            {
                                path: Routenames.projects,
                                canActivate: [requireAnyFunctionality(...projectOverviewAccess)],
                                data: {
                                    menuId: Routenames.projects,
                                    search: desktopSearch,
                                },
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        component: CustomerProjectOverviewComponent,
                                        data: { title: 'Projecten' },
                                    },
                                    {
                                        path: Routenames.contacts,
                                        canActivate: [requireAnyFunctionality(...projectAccess)],
                                        component: ContactOverviewComponent,
                                        data: { title: 'Selecteer project' },
                                    },
                                    {
                                        path: `:${RouteParamnames.projectId}`,
                                        data: { title: 'Project', search: false },
                                        canActivate: [requireAnyFunctionality(...projectAccess)],
                                        children: [
                                            { path: '', pathMatch: 'full', component: CustomerProjectDetailComponent },
                                            {
                                                path: Routenames.edit,
                                                component: CustomerProjectManageComponent,
                                                canActivate: [
                                                    requireAnyFunctionality(Functionalities.CustomerProjectsManage),
                                                ],
                                                data: { title: 'Project beheren', search: false },
                                            },
                                            {
                                                path: Routenames.requestAccess,
                                                component: CustomerProjectRequestAccessComponent,
                                                canActivate: [
                                                    requireAnyFunctionality(
                                                        Functionalities.CustomerProjectsParticipate,
                                                        Functionalities.CustomerProjectsManage
                                                    ),
                                                ],
                                                data: { title: 'Toegang aanvragen', search: false },
                                            },
                                            {
                                                path: Routenames.consumers,
                                                component: CustomerProjectConsumersComponent,
                                                data: { title: 'Aanmeldingen bewoners', search: true },
                                            },
                                            {
                                                path: Routenames.contacts,
                                                data: { title: 'Contactpersonen', search: true },
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        component: ContactOverviewComponent,
                                                    },
                                                    {
                                                        path: `:${RouteParamnames.contactId}`,
                                                        data: { title: 'Contactpersoon', search: false },
                                                        children: [
                                                            {
                                                                path: '',
                                                                pathMatch: 'full',
                                                                component: ContactDetailComponent,
                                                            },
                                                            {
                                                                path: Routenames.edit,
                                                                component: ContactEditComponent,
                                                                canDeactivate: [CanDeactivateForm],
                                                                data: { title: 'Contactpersoon muteren' },
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: Routenames.drillingProjects,
                                canActivate: [requireAnyFunctionality(...projectAccess)],
                                data: { title: 'Booropdrachten', menuId: Routenames.projects, search: desktopSearch },
                                children: [
                                    { path: '', pathMatch: 'full', component: CustomerProjectOverviewComponent },
                                    {
                                        path: `:${RouteParamnames.projectId}`,
                                        component: CustomerProjectDetailComponent,
                                        data: { title: 'Booropdracht', search: false },
                                    },
                                    {
                                        path: `:${RouteParamnames.projectId}/${Routenames.edit}`,
                                        component: CustomerProjectManageComponent,
                                        canActivate: [requireAnyFunctionality(Functionalities.CustomerProjectsManage)],
                                        data: { title: 'Booropdracht beheren', search: false },
                                    },
                                    {
                                        path: `:${RouteParamnames.projectId}/${Routenames.requestAccess}`,
                                        component: CustomerProjectRequestAccessComponent,
                                        canActivate: [
                                            requireAnyFunctionality(
                                                Functionalities.CustomerProjectsParticipate,
                                                Functionalities.CustomerProjectsManage
                                            ),
                                        ],
                                        data: { title: 'Toegang aanvragen', search: false },
                                    },
                                ],
                            },
                            {
                                path: '',
                                component: CustomerDetailComponent,
                                data: { title: 'Kaart', menuId: Routenames.customers, header: false },
                                children: [
                                    {
                                        path: Routenames.workareas,
                                        children: [
                                            {
                                                path: `:${RouteParamnames.workareaId}`,
                                                data: { title: 'Workarea' },
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        component: WorkareaComponent,
                                                        canDeactivate: [CanDeactivateForm],
                                                    },
                                                    {
                                                        path: Routenames.documents,
                                                        component: DocumentsComponent,
                                                        data: { title: 'Documenten' },
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: Routenames.airtable,
                component: AirtableMapComponent,
                canActivate: [requireAnyFunctionality(Functionalities.Airtable)],
                data: {
                    title: 'Commercie',
                    search: false,
                    menu: {
                        id: Routenames.airtable,
                        label: 'Commercie',
                        icon: 'map',
                        visible: () =>
                            !!LocalStorage.getUser() &&
                            AuthorisationService.hasFunctionality(Functionalities.Airtable) &&
                            desktopOnly(),
                    },
                },
            },
            {
                path: Routenames.addresses,
                component: AddressesMapComponent,
                canActivate: [requireAnyFunctionality(Functionalities.Adresses)],
                data: {
                    title: 'Adressen',
                    search: false,
                    menu: {
                        id: Routenames.addresses,
                        label: 'Adressen',
                        icon: 'map',
                        visible: () =>
                            !!LocalStorage.getUser() &&
                            AuthorisationService.hasFunctionality(Functionalities.Adresses) &&
                            desktopOnly(),
                    },
                },
            },
            {
                path: Routenames.projects,
                component: ProjectsComponent,
                canActivate: [requireAnyFunctionality(...projectAccess)],
                data: {
                    title: 'Projecten',
                    search: desktopSearch,
                    menu: {
                        id: Routenames.projects,
                        label: 'Projecten',
                        icon: 'guide',
                        // Same check as canActivate, otherwise the menu links to a page that redirects away.
                        visible: () => AuthorisationService.hasAnyFunctionality(projectAccess),
                    },
                },
            },
            {
                path: Routenames.timeline,
                pathMatch: 'full',
                component: TimelineComponent,
                canActivate: [requireAnyFunctionality(Functionalities.Timeline)],
                data: {
                    title: 'Tijdlijn',
                    search: desktopSearch,
                    menu: {
                        id: Routenames.timeline,
                        label: 'Tijdlijn',
                        icon: 'timeline',
                        visible: () => AuthorisationService.hasFunctionality(Functionalities.Timeline),
                    },
                },
            },
            {
                path: Routenames.timeline,
                component: CustomerTimelineComponent,
                canActivate: [requireAnyFunctionality(Functionalities.Timeline)],
                data: { title: 'Tijdlijn', menuId: Routenames.timeline, search: desktopSearch },
                children: [
                    {
                        path: `:${RouteParamnames.timelineId}`,
                        children: [
                            { path: '', pathMatch: 'full', component: TimelineDetailComponent },
                            {
                                path: Routenames.edit,
                                canActivate: [requireAnyFunctionality(Functionalities.TimelineManagement)],
                                component: TimelineEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Tijdlijn bewerken' },
                            },
                            {
                                path: Routenames.copy,
                                canActivate: [requireAnyFunctionality(Functionalities.TimelineManagement)],
                                component: TimelineEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Tijdlijn kopiëren' },
                            },
                        ],
                    },
                ],
            },
            {
                path: Routenames.chat,
                canActivate: [requireFeature(Features.Chats)],
                data: {
                    title: () => (AuthorisationService.hasRole(Roles.Consumer) ? 'Contact' : 'Meldingen'),
                    search: false,
                    menu: {
                        id: Routenames.chat,
                        label: () => (AuthorisationService.hasRole(Roles.Consumer) ? 'Contact' : 'Meldingen'),
                        icon: 'chat',
                        visible: () => AuthorisationService.hasFeature(Features.Chats),
                    },
                },
                children: [
                    { path: '', pathMatch: 'full', component: ChatComponent },
                    { path: `:${RouteParamnames.chatId}`, component: ChatComponent, data: { title: 'Melding' } },
                ],
            },
            {
                path: Routenames.news,
                component: NewsComponent,
                canActivate: [requireFeature(Features.News)],
                data: {
                    title: 'Updates',
                    search: false,
                    menu: {
                        id: Routenames.news,
                        label: 'Updates',
                        icon: 'updates',
                        visible: () => AuthorisationService.hasFeature(Features.News),
                    },
                },
                children: [
                    { path: Routenames.future, component: NewsComponent, data: { title: 'Toekomstige updates' } },
                    {
                        path: `:${RouteParamnames.newsId}`,
                        data: { title: 'Update' },
                        children: [
                            { path: '', pathMatch: 'full', component: NewsDetailComponent },
                            {
                                path: Routenames.edit,
                                component: NewsEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Update bewerken' },
                            },
                            {
                                path: Routenames.copy,
                                component: NewsEditComponent,
                                canDeactivate: [CanDeactivateForm],
                                data: { title: 'Update kopiëren' },
                            },
                            {
                                path: Routenames.survey,
                                children: [
                                    {
                                        path: Routenames.edit,
                                        component: SurveyEditComponent,
                                        canDeactivate: [CanDeactivateForm],
                                        data: { title: 'Vragenlijst bewerken' },
                                    },
                                    {
                                        path: Routenames.answers,
                                        component: SurveyAnswersComponent,
                                        data: { title: 'Vragenlijst antwoorden' },
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: Routenames.settings,
                component: SettingsComponent,
                data: {
                    title: 'Profiel',
                    search: false,
                    menu: { id: Routenames.settings, label: 'Profiel', icon: 'profile' },
                },
            },
            {
                path: Routenames.importShapefile,
                component: ImportShapefileComponent,
                canActivate: [requireAnyFunctionality(Functionalities.CustomerProjectsManage)],
                data: { title: 'Shapefile importeren', search: true },
            },
            {
                path: Routenames.importExcel,
                component: ImportExcelComponent,
                canActivate: [requireAnyFunctionality(Functionalities.CustomerProjectsManage)],
                data: { title: 'Excel importeren', search: true },
            },
        ],
    },

    // Legacy route aliases. Deze blijven staan zolang er nog niet-bijgewerkte backends
    // (test, acceptatie, productie) de oude deeplinks kunnen versturen. Verwijder ze pas
    // gezamenlijk nadat de backendomgeving de actuele URL's produceert.
    { path: 'login', redirectTo: Routenames.login, pathMatch: 'full' },
    {
        path: 'towns/:townId/projects/:projectId/contacts/edit/:id',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.projects}/:projectId/${Routenames.contacts}/:id/${Routenames.edit}`,
        pathMatch: 'full',
    },
    {
        path: 'towns/:townId/projects/:projectId/tijdlijn/:id',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.projects}/:projectId/${Routenames.timeline}/:id`,
        pathMatch: 'full',
    },
    {
        path: 'towns/:townId/projects/:projectId/tijdlijn/:id/bewerken',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.projects}/:projectId/${Routenames.timeline}/:id/${Routenames.edit}`,
        pathMatch: 'full',
    },
    {
        path: 'towns/:townId/projects/:projectId/tijdlijn/:id/kopie',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.projects}/:projectId/${Routenames.timeline}/:id/${Routenames.copy}`,
        pathMatch: 'full',
    },
    {
        path: 'towns/:townId/projects-drilling',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.drillingProjects}`,
        pathMatch: 'full',
    },
    {
        path: 'towns/:townId/projects-drilling/:projectId',
        redirectTo: `${Routenames.customers}/:townId/${Routenames.drillingProjects}/:projectId`,
        pathMatch: 'full',
    },
    { path: 'meldingen', redirectTo: Routenames.chat, pathMatch: 'full' },
    { path: 'meldingen/:id', redirectTo: `${Routenames.chat}/:id`, pathMatch: 'full' },
    { path: 'updates', redirectTo: Routenames.news, pathMatch: 'full' },
    { path: 'updates/toekomst', redirectTo: `${Routenames.news}/${Routenames.future}`, pathMatch: 'full' },
    { path: 'updates/:id', redirectTo: `${Routenames.news}/:id`, pathMatch: 'full' },
    { path: 'updates/:id/bewerken', redirectTo: `${Routenames.news}/:id/${Routenames.edit}`, pathMatch: 'full' },
    { path: 'updates/:id/kopie', redirectTo: `${Routenames.news}/:id/${Routenames.copy}`, pathMatch: 'full' },
    {
        path: 'updates/:id/vragenlijst-bewerken',
        redirectTo: `${Routenames.news}/:id/${Routenames.survey}/${Routenames.edit}`,
        pathMatch: 'full',
    },
    {
        path: 'updates/:id/vragenlijst-antwoorden',
        redirectTo: `${Routenames.news}/:id/${Routenames.survey}/${Routenames.answers}`,
        pathMatch: 'full',
    },
    { path: 'tijdlijn', redirectTo: Routenames.timeline, pathMatch: 'full' },
    { path: 'tijdlijn/:id', redirectTo: `${Routenames.timeline}/:id`, pathMatch: 'full' },
    { path: 'tijdlijn/:id/bewerken', redirectTo: `${Routenames.timeline}/:id/${Routenames.edit}`, pathMatch: 'full' },
    { path: 'tijdlijn/:id/kopie', redirectTo: `${Routenames.timeline}/:id/${Routenames.copy}`, pathMatch: 'full' },

    // Hernoemde segmenten, als prefix. Zonder pathMatch matcht Angular alleen het eerste
    // segment en plakt het restant van de URL achter de bestemming: towns/1/workareas/2
    // wordt customers/1/workareas/2. Deze staan na de aliassen hierboven, want die
    // hernoemen ook segmenten midden in het pad en moeten dus eerst matchen.
    { path: 'towns', redirectTo: Routenames.customers },
    { path: 'towns-screenshot', redirectTo: Routenames.customersScreenshot },
    { path: 'articles', redirectTo: Routenames.news },
];

export const routingConfiguration: ExtraOptions = {
    paramsInheritanceStrategy: 'always',
};

@NgModule({
    exports: [RouterModule],
    imports: [RouterModule.forRoot(routes, routingConfiguration)],
})
export class AppRoutingModule {}
